import { RefObject, useEffect, useRef, useState } from 'react'

import { isDev } from '@/shared/lib/debug/utils'

import { hasInjectedContent } from '../utils/has-injected-content'

type UseWidgetEmbeddedContentParams = {
	targetRef: RefObject<HTMLElement>
}

export const useWidgetEmbeddedContent = ({ targetRef }: UseWidgetEmbeddedContentParams): boolean => {
	const [isContentInjected, setIsContentInjected] = useState(false)
	const hasEverBeenInjectedRef = useRef(false)
	const lastContentStateRef = useRef(false)

	useEffect(() => {
		const target = targetRef.current
		if (!target) {
			if (isDev) {
				console.debug('[BonusConversionWidget] useWidgetEmbeddedContent: targetRef.current is null')
			}
			return
		}

		const getSnapshot = () => {
			const hasChildren = target.childElementCount > 0
			const text = target.textContent?.trim() ?? ''
			return {
				hasChildren,
				hasText: !!text,
				textLength: text.length,
				childCount: target.childElementCount
			}
		}

		let hideTimer: ReturnType<typeof setTimeout> | null = null
		const clearHideTimer = () => {
			if (hideTimer) {
				clearTimeout(hideTimer)
				hideTimer = null
			}
		}

		const setContentState = (nextState: boolean, reason: string) => {
			if (lastContentStateRef.current === nextState) {
				return
			}

			if (isDev) {
				console.debug(`[BonusConversionWidget] setContentState(${reason})`, {
					prev: lastContentStateRef.current,
					next: nextState,
					...getSnapshot()
				})
			}
			lastContentStateRef.current = nextState
			setIsContentInjected(nextState)
		}

		const commitState = () => {
			clearHideTimer()
			const hasContent = hasInjectedContent(target)
			if (!hasContent) {
				if (hasEverBeenInjectedRef.current) {
					return
				}

				hideTimer = setTimeout(() => {
					const hasContentAfterDelay = hasInjectedContent(target)
					if (isDev) {
						console.debug('[BonusConversionWidget] delayed-hide recheck', {
							willHide: !hasContentAfterDelay,
							...getSnapshot()
						})
					}
					if (!hasContentAfterDelay && !hasEverBeenInjectedRef.current) {
						setContentState(false, 'hide')
					}
				}, 120)
				return
			}

			hasEverBeenInjectedRef.current = true
			setContentState(true, 'show')
		}

		if (isDev) {
			console.debug('[BonusConversionWidget] useWidgetEmbeddedContent: start observing', {
				...getSnapshot()
			})
		}

		commitState()

		const observer = new MutationObserver((mutations: MutationRecord[]) => {
			if (isDev) {
				console.debug('[BonusConversionWidget] mutations', {
					count: mutations.length,
					types: [...new Set(mutations.map(item => item.type))],
					nodes: mutations.map(item => ({
						type: item.type,
						target: item.target.nodeName,
						attributeName: item.attributeName
					}))
				})
			}
			commitState()
		})
		observer.observe(target, {
			childList: true,
			subtree: true,
			characterData: true,
			attributes: true,
			attributeOldValue: true,
			attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled']
		})

		return () => {
			if (isDev) {
				console.debug('[BonusConversionWidget] useWidgetEmbeddedContent: stop observing')
			}
			observer.disconnect()
			clearHideTimer()
		}
	}, [targetRef])

	return isContentInjected
}
